import { ButtonGroup, Icon, Slider, Text, TextButton } from '@oneaudi/unified-web-components';
import { useCallback, useState } from 'react';
import { useI18n } from '~/app/i18n/i18n';
import {
	MatchFilter,
	PriceFilter,
} from '~/app/pages/configurator/components/dialogs/car-matching-results-dialog/models/types';
import { PriceFilterStyle } from '~/app/pages/configurator/components/dialogs/car-matching-results-dialog/styles/price-filter-styles';
import {
	PopoverContentStyle,
	StyledPopover,
} from '~/app/pages/configurator/components/dialogs/car-matching-results-dialog/styles/styled-popover';
import { appTrackingService } from '~/app/tracking/app-tracking-service';
import { EventAction } from '~/app/tracking/feld-m-types';
import { useCurrencyFormatter } from '~/hooks/use-currency-formatter';

export default function PriceFilterPopover({
	priceFilterIndicator,
	priceRange,
	minPrice,
	maxPrice,
	setPriceRange,
	setupMatchFilter,
	priceDefaultValue,
}: {
	priceFilterIndicator: string;
	priceRange: PriceFilter;
	minPrice: number;
	maxPrice: number;
	setPriceRange: (priceRange: PriceFilter) => void;
	setupMatchFilter: (value: Partial<MatchFilter>) => void;
	priceDefaultValue: PriceFilter;
}) {
	const { i18n } = useI18n();
	const { currency } = useCurrencyFormatter();
	const [isOpen, setIsOpen] = useState(false);

	const trackPriceFilter = useCallback((label: string, values: string[]) => {
		appTrackingService.stockCarClickEvent({
			eventAction: EventAction.InternalLink,
			eventName: 'configurator (stock car matching) - change filter',
			label,
			elementName: 'button',
			relatedFilters: [
				{
					id: 'price',
					values,
				},
			],
		});
	}, []);

	return (
		<PriceFilterStyle>
			<StyledPopover onClick={() => setIsOpen(!isOpen)}>
				<button className="popover--trigger">
					{`${i18n('ngc.stockcar.matches.filter.price.trigger')} ${priceFilterIndicator}`}{' '}
					<Icon className="icon" name="arrow-down" size="s" />
				</button>
			</StyledPopover>

			<PopoverContentStyle $show={isOpen}>
				<Text variant={{ type: 'copy', order: '1', style: 'normal' }}>
					{i18n('ngc.stockcar.matches.filter.price.title')} ({currency()})
				</Text>
				<Slider
					id="multi-slider__basic"
					min={minPrice}
					max={maxPrice}
					step={5000}
					value={priceRange}
					rangeSeparator={i18n('ngc.stockcar.matches.filter.price.rangeSeparator')}
					onChange={(prices) => setPriceRange(prices as PriceFilter)}
					aria-label={i18n('ngc.stockcar.matches.filter.price.title')}
				/>
				<ButtonGroup>
					<TextButton
						onClick={() => {
							setupMatchFilter({ price: priceDefaultValue });
							setPriceRange(priceDefaultValue);
							setIsOpen(false);
							trackPriceFilter(
								i18n('ngc.actions.reset'),
								priceDefaultValue.map((price) => price.toString()),
							);
						}}
						size="sm"
						variant="secondary">
						{i18n('ngc.actions.reset')}
					</TextButton>
					<TextButton
						onClick={() => {
							setupMatchFilter({ price: priceRange });
							setIsOpen(false);
							trackPriceFilter(
								i18n('ngc.actions.apply'),
								priceRange.map((price) => price.toString()),
							);
						}}
						size="sm"
						variant="primary">
						{i18n('ngc.actions.apply')}
					</TextButton>
				</ButtonGroup>
			</PopoverContentStyle>
		</PriceFilterStyle>
	);
}
